import React from 'react';
import {
  MagnifyingGlassIcon,
  CalendarIcon,
  Squares2X2Icon,
  ArrowsUpDownIcon
} from '@heroicons/react/24/outline';
import { Input, InputGroup } from './input';
import { Fieldset, FieldGroup, Field, Label } from './fieldset';
import { FilterButton } from './filter-button';

// Common date filter options (used by both Documents and Workspaces pages)
export const DATE_FILTER_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Today', value: 'Today' },
  { label: 'Yesterday', value: 'Yesterday' },
  { label: 'Past week', value: 'Past week' },
  { label: 'Past month', value: 'Past month' },
  { label: 'Past year', value: 'Past year' }
];

export type FilterConfig<T = string> = {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  multiple?: boolean;
  initialValues?: T[];
  values: { label: string; value: T }[];
  onChange: (values: T[]) => void;
};

export type FilterBarProps = {
  // Search
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;

  // Common filters (optional)
  dateFilter?: {
    value: string | undefined;
    onChange: (value: string | undefined) => void;
  };

  groupBy?: {
    value: string | undefined;
    options: { label: string; value: string }[];
    onChange: (value: string | undefined) => void;
  };

  sortBy?: {
    value: string | undefined;
    options: { label: string; value: string }[];
    onChange: (value: string | undefined) => void;
  };

  // Custom filters (for unique filters like Status, User, Preset)
  customFilters?: FilterConfig[];

  // Show filters only after data loads
  showFilters?: boolean;
};

export const FilterBar: React.FC<FilterBarProps> = ({
  search,
  onSearchChange,
  searchPlaceholder = 'Start typing to filter...',
  dateFilter,
  groupBy,
  sortBy,
  customFilters = [],
  showFilters = true
}) => {
  return (
    <form className="w-full" onSubmit={(e) => e.preventDefault()}>
      <Fieldset>
        {/* Search Input - Always visible */}
        <FieldGroup>
          <Field className="w-full max-w-md">
            <Label className="sr-only" data-slot="none">
              Filter
            </Label>
            <InputGroup>
              <MagnifyingGlassIcon className="h-4 w-4" />
              <Input
                type="search"
                name="search"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => onSearchChange(e.currentTarget.value)}
              />
            </InputGroup>
          </Field>
        </FieldGroup>

        {/* Filter Buttons - Only show when data is loaded */}
        {showFilters && (
          <div className="mt-2 flex flex-row flex-wrap gap-2 items-center">
            {/* Custom filters (Status, User, Preset, etc.) */}
            {customFilters.map((filter) => (
              <Field key={filter.label}>
                <Label className="sr-only" data-slot="none">
                  Filter by {filter.label.toLowerCase()}
                </Label>
                <FilterButton
                  label={filter.label}
                  icon={filter.icon}
                  multiple={filter.multiple}
                  initialValues={filter.initialValues}
                  values={filter.values}
                  onChange={filter.onChange}
                />
              </Field>
            ))}

            {/* Date Filter */}
            {dateFilter && (
              <Field>
                <Label className="sr-only" data-slot="none">
                  Filter by date
                </Label>
                <FilterButton
                  label="Date"
                  icon={CalendarIcon}
                  initialValues={dateFilter.value ? [dateFilter.value] : []}
                  values={DATE_FILTER_OPTIONS}
                  onChange={(values) => dateFilter.onChange(values[0])}
                />
              </Field>
            )}

            {/* Group By */}
            {groupBy && (
              <Field>
                <Label className="sr-only" data-slot="none">
                  Group by field
                </Label>
                <FilterButton
                  label="Group"
                  icon={Squares2X2Icon}
                  initialValues={groupBy.value ? [groupBy.value] : []}
                  values={groupBy.options}
                  onChange={(values) => groupBy.onChange(values[0])}
                />
              </Field>
            )}

            {/* Sort By */}
            {sortBy && (
              <Field>
                <Label className="sr-only" data-slot="none">
                  Sort by field
                </Label>
                <FilterButton
                  label="Sort"
                  icon={ArrowsUpDownIcon}
                  initialValues={sortBy.value ? [sortBy.value] : []}
                  values={sortBy.options}
                  onChange={(values) => sortBy.onChange(values[0])}
                />
              </Field>
            )}
          </div>
        )}
      </Fieldset>
    </form>
  );
};
